import { derived } from 'svelte/store';
import {
  createMutation,
  createQuery,
  type QueryClient,
} from '$lib/api/tanstack-query';

import type * as APITypes from '@viamrobotics/web-client/app/v1/end_user';
import type { EndUserServiceClient } from '@viamrobotics/web-client/app/v1/end_user.client';

import { type AuthContext, useAuthContext } from '$lib/auth';

import { useEndUserClient } from './client';
import { useQueryInvalidator } from './invalidate';
import { prefetchQuery } from './prefetch';
import {
  type BaseQueryOptions,
  createQueryOptions,
  getAuthedQueryOptions,
  useAuthedQueryOptions,
} from './query-options';

const endUserQueryKeys = {
  isLegalAccepted: () => ['isLegalAccepted'],
};

const isLegalAcceptedQueryOptions = ({
  client,
  getRPCOptions,
  enabled,
}: BaseQueryOptions<EndUserServiceClient>) =>
  createQueryOptions({
    enabled,
    queryKey: endUserQueryKeys.isLegalAccepted(),
    queryFn: async () => {
      const rpcOptions = await getRPCOptions();
      const { response } = await client.isLegalAccepted({}, rpcOptions);

      return response;
    },
  });

export const createIsLegalAcceptedQuery = () => {
  const baseQueryOptions = useAuthedQueryOptions('endUser');
  const queryOptions = derived(baseQueryOptions, (baseOptions) =>
    isLegalAcceptedQueryOptions(baseOptions)
  );

  return createQuery(queryOptions);
};

export const createAcceptLegalMutation = () => {
  const endUserClient = useEndUserClient();
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async (request: APITypes.AcceptLegalRequest) => {
      const rpcOptions = await getRPCOptions();
      const { response } = await endUserClient.acceptLegal(request, rpcOptions);

      return response;
    },
    onSuccess: async () => {
      await invalidateQueries([endUserQueryKeys.isLegalAccepted()]);
    },
  });
};

export const prefetchIsLegalAccepted = async (
  queryClient: QueryClient,
  client: EndUserServiceClient,
  authContext: AuthContext
) => {
  const baseOptions = getAuthedQueryOptions(client, authContext);
  const options = isLegalAcceptedQueryOptions(baseOptions);

  return prefetchQuery(queryClient, options);
};
